import { FormProperty, SchemaValidatorFactory, PropertyGroup, FormPropertyFactory } from "./angular2-schema-form-shim";
import { SchemaErrorDetail } from 'z-schema';

import { NumberProperty as _NumberProperty } from "angular2-schema-form/src/model/numberproperty";
import { StringProperty as _StringProperty } from "angular2-schema-form/src/model/stringproperty";
import { BooleanProperty as _BooleanProperty } from "angular2-schema-form/src/model/booleanproperty";
import { ObjectProperty as _ObjectProperty } from "angular2-schema-form/src/model/objectproperty";
import { ArrayProperty as _ArrayProperty } from "angular2-schema-form/src/model/arrayproperty";
import { SPSchemaValidatorFactory } from "./schemavalidatorfactory";
import { JSONSchema } from "./types/json-schema";

export function PropertyOverrides<T extends { new( ...args: any[] ): FormProperty }>( base: T ) {
  return class extends base {

    schema: JSONSchema;

    schemaValidatorFactory: SPSchemaValidatorFactory;
    isObject: boolean;

    constructor( ...args: any[] ) {
      super( ...args );

      this.checkSchema();

      // Object and Array properties pass the first property as
      if ( args[ 0 ] instanceof FormPropertyFactory ) {
        let formPropertyFactory: FormPropertyFactory = args.shift();
      }

      this.schemaValidatorFactory = args[ 0 ];
      let parent: PropertyGroup = args[ 3 ];
      let path: string = args[ 4 ];

      this.schemaValidator = this.extendValidator( this.schemaValidatorFactory.createValidatorFn( this.schema ) );
    }

    checkSchema() {
      if ( !this.schema ) throw new Error( 'Property has no schema' );

      if ( !( 'options' in this.schema ) ) this.schema.options = {};
    }

    extendValidator( schemaValidatorFn: ( value ) => SchemaErrorDetail[] ) {
      return ( value ): SchemaErrorDetail[] => {
        let err = schemaValidatorFn( value );

        if ( err && err.length ) {
          // Ignore errors on hidden properties
          err = err.filter( error => {
            let property = this.searchProperty( error.path.substring( 1 ) );
            return !property || property.visible;
          } );
        }

        return err || null;
      };
    }

    public _runValidation(): any {
      if ( this.isObject ) super._runValidation();
      let errors = this.schemaValidator( this.value ) || [];
      let customValidators = this.validatorRegistry.get( this.path );
      if ( customValidators && !this.schemaValidatorFactory.isPropertyMissing( this.value, this.schema ) ) {
        // Remove null child properties, fix numeric types
        let cleanedValue = this.schemaValidatorFactory.cleanupValue( this.value, this.schema );
        let customErrors = customValidators.map( customValidator => customValidator( cleanedValue, this, this.findRoot() ) );
        errors = this.mergeErrors( errors, customErrors );
      }
      if ( errors.length === 0 ) {
        errors = null;
      }

      this._errors = errors;
      this.setErrors( this._errors );
    }

    public mergeErrors( errors, newErrors ) {
      if ( newErrors ) {
        if ( Array.isArray( newErrors ) ) {
          errors = errors.concat( ...newErrors );
        } else {
          errors.push( newErrors );
        }
        errors = this.uniqueErrors( errors );
      }
      return errors;
    }

    uniqueErrors( errors: SchemaErrorDetail[] ): SchemaErrorDetail[] {
      // Errors are unique based on the path and code
      let uniqueErrorMap: { [ uniqueKey: string ]: SchemaErrorDetail } = {};
      errors.forEach( error => error && ( uniqueErrorMap[ `${error.path}\n${error.code}` ] = error ) );
      return Object.values( uniqueErrorMap );
    }

    // Make sure the abstract methods are defined
    public setValue( value: any, onlySelf: boolean ) {
      base.prototype.setValue.call( this, value, onlySelf );
    }
    public reset( value: any, onlySelf: boolean ) {
      base.prototype.reset.call( this, value, onlySelf );
    }
    public _hasValue(): boolean {
      return base.prototype._hasValue.call( this );
    }
    public _updateValue() {
      base.prototype._updateValue.call( this );
    }

    public exportValue() {
      // We don't actually want to override this method, just provide a stub to prevent errors.
      if ( 'exportValue' in base.prototype ) {
        return base.prototype.exportValue.call( this );
      } else {
        return this.value;
      }
    }
  }
}

@PropertyOverrides export class NumberProperty extends _NumberProperty { }
@PropertyOverrides export class StringProperty extends _StringProperty { }
@PropertyOverrides export class BooleanProperty extends _BooleanProperty { }
@PropertyOverrides export class ArrayProperty extends _ArrayProperty { }

@PropertyOverrides export class ObjectProperty extends _ObjectProperty {
  schema: JSONSchema

  isObject = true

  public exportValue() {
    const value: { [ key: string ]: any } = {};

    for ( const propertyName in this.schema.properties ) {
      value[ propertyName ] = typeof this._value[ propertyName ] === 'undefined'
        ? null
        : this._value[ propertyName ];
    }

    return value;
  }
}



// WEBPACK FOOTER //
// /var/www/schemas-forms/src/app/formproperty.ts